/**
 * Google Analytics 4 Measurement Protocol (server-side tracking)
 */

import type { EcommerceItem } from './types'

interface GA4ServerEventParams {
  client_id: string
  user_id?: string
  transaction_id: string
  value: number
  currency: string
  items: EcommerceItem[]
  coupon?: string
  session_id?: string
  page_location?: string // Landing page URL
  page_title?: string // Landing page title
}

/**
 * Send purchase event to GA4 via Measurement Protocol
 * This should be called from the server (API routes) after payment confirmation
 *
 * @param measurementId - GA4 Measurement ID (e.g., "G-XXXXXXXXXX")
 * @param apiSecret - Google Measurement API Secret (get from GA4 Admin > Data Streams > Measurement Protocol)
 * @param params - Event parameters
 */
export async function sendGA4PurchaseEvent(
  measurementId: string,
  apiSecret: string,
  params: GA4ServerEventParams,
): Promise<boolean> {
  if (!measurementId || !apiSecret) {
    console.warn('GA4 Measurement ID or API Secret not configured')
    return false
  }

  const url = `https://www.google-analytics.com/mp/collect?measurement_id=${measurementId}&api_secret=${apiSecret}`

  const payload = {
    client_id: params.client_id,
    ...(params.user_id ? { user_id: params.user_id } : {}),
    events: [
      {
        name: 'purchase',
        params: {
          transaction_id: params.transaction_id,
          value: params.value,
          currency: params.currency,
          items: params.items,
          ...(params.coupon ? { coupon: params.coupon } : {}),
          ...(params.session_id ? { session_id: params.session_id } : {}),
          ...(params.page_location
            ? { page_location: params.page_location }
            : {}),
          ...(params.page_title ? { page_title: params.page_title } : {}),
        },
      },
    ],
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const text = await response.text()
      console.error('GA4 Measurement Protocol error:', response.status, text)
      return false
    }

    console.log('GA4 purchase event sent successfully (server-side):', {
      transaction_id: params.transaction_id,
      value: params.value,
      items: params.items.length,
    })

    return true
  } catch (error) {
    console.error('Failed to send GA4 purchase event:', error)
    return false
  }
}

/**
 * Send custom event to GA4 via Measurement Protocol
 */
export async function sendGA4CustomEvent(
  measurementId: string,
  apiSecret: string,
  clientId: string,
  eventName: string,
  eventParams: Record<string, any>,
  userId?: string,
): Promise<boolean> {
  if (!measurementId || !apiSecret) {
    console.warn('GA4 Measurement ID or API Secret not configured')
    return false
  }

  const url = `https://www.google-analytics.com/mp/collect?measurement_id=${measurementId}&api_secret=${apiSecret}`

  const payload = {
    client_id: clientId,
    ...(userId ? { user_id: userId } : {}),
    events: [
      {
        name: eventName,
        params: eventParams,
      },
    ],
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const text = await response.text()
      console.error('GA4 Measurement Protocol error:', response.status, text)
      return false
    }

    console.log(`GA4 ${eventName} event sent successfully (server-side)`)
    return true
  } catch (error) {
    console.error(`Failed to send GA4 ${eventName} event:`, error)
    return false
  }
}
